import { useEffect, useMemo, useState } from 'react';
import { MessagesSquare, Megaphone, X, Check, UserRound, ChevronDown } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { SheetHeader, SheetTitle } from '../../../components/ui/sheet';
import SafeImage from '../../../components/SafeImage';
import { slotsAgentUnreadKey } from '../../../services/chatAgentIdentity';
import {
  FacebookGlyph, DiscordGlyph, TelegramGlyph,
  InstagramGlyph, YoutubeGlyph, TikTokGlyph, WhatsAppGlyph,
} from './Glyphs';

/* -------------------- METADATA + STORAGE -------------------- */
const PREFERRED_META = {
  instagram: { label: 'Instagram', gradient: 'linear-gradient(135deg, #004D61, #006B85 52%, #004D61 100%)', Glyph: InstagramGlyph, cta: 'Message' },
  youtube:   { label: 'YouTube',   gradient: '#FF0000', Glyph: YoutubeGlyph, cta: 'Open' },
  tiktok:    { label: 'TikTok',    gradient: '#000000', Glyph: TikTokGlyph,  cta: 'Open' },
  facebook:  { label: 'Facebook',  gradient: '#1877F2', Glyph: FacebookGlyph, cta: 'Message' },
  whatsapp:  { label: 'WhatsApp',  gradient: '#25D366', Glyph: WhatsAppGlyph, cta: 'Chat' },
  discord:   { label: 'Discord',   gradient: '#5865F2', Glyph: DiscordGlyph,  cta: 'Join' },
  telegram:  { label: 'Telegram',  gradient: '#26A5E4', Glyph: TelegramGlyph, cta: 'Chat' },
  support_email: { label: 'Email', gradient: 'linear-gradient(135deg, #004D61, #00A86B)', Glyph: null, cta: 'Email' },
};

const PREFERRED_STORAGE_KEY = 'teamlobby_preferred_support_channels';
const SUPPORT_ACCENT = 'var(--vl-profile-accent, var(--vl-neon, #004D61))';
const SUPPORT_ACCENT_TEXT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 72%, #FFFFFF)';
const SUPPORT_ACCENT_DIM = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 14%, transparent)';
const SUPPORT_ACCENT_SOFT = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 22%, transparent)';
const SUPPORT_ACCENT_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 48%, transparent)';
const SUPPORT_ACCENT_STRONG_BORDER = 'color-mix(in oklab, var(--vl-profile-accent, var(--vl-neon, #004D61)) 68%, transparent)';
const SUPPORT_ACCENT_GLOW = 'transparent';
const SUPPORT_ACCENT_GRADIENT = 'linear-gradient(135deg, var(--vl-btn-primary-from, var(--vl-profile-accent, var(--vl-neon, #004D61))), var(--vl-btn-primary-to, #004D61))';
const SUPPORT_ONLINE_GREEN = '#22C55E';
const SUPPORT_ONLINE_BORDER = 'rgba(255,255,255,0.14)';
const SUPPORT_ONLINE_GLOW = 'transparent';
const MAIN_AGENT_MASCOT_AVATAR = '/assets/player-v2/mascots/slots-oceans-main-agent-shark-v1.png';

/** Tiny cluster badge: FB + IG + WhatsApp overlapping mini-circles. */
function SocialCluster() {
  const Circle = ({ bg, children, style }) => (
    <span
      className="absolute w-5 h-5 rounded-full flex items-center justify-center"
      style={{
        background: bg,
        boxShadow: '0 2px 6px rgba(0,0,0,0.45), 0 0 0 1.5px rgba(11,6,24,0.9)',
        ...style,
      }}
    >
      {children}
    </span>
  );
  return (
    <span className="relative w-9 h-7 block" aria-hidden>
      <Circle bg="#1877F2" style={{ left: 0, top: 2 }}>
        <svg viewBox="0 0 24 24" width="12" height="12" fill="#fff"><path d="M13.5 21v-7.5h2.5l.4-3H13.5V8.5c0-.9.3-1.5 1.6-1.5H16.6V4.3c-.3 0-1.3-.1-2.4-.1-2.4 0-4 1.5-4 4.1V10.5H7.8v3h2.4V21h3.3z"/></svg>
      </Circle>
      <Circle bg="linear-gradient(135deg, #004D61, #006B85 52%, #004D61 100%)" style={{ left: 8, top: 0, zIndex: 2 }}>
        <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#fff" strokeWidth="2">
          <rect x="3.5" y="3.5" width="17" height="17" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <circle cx="17.3" cy="6.7" r="1" fill="#fff" stroke="none" />
        </svg>
      </Circle>
      <Circle bg="#25D366" style={{ left: 16, top: 2, zIndex: 3 }}>
        <svg viewBox="0 0 32 32" width="12" height="12" fill="#fff">
          <path d="M16 3C9 3 3.3 8.6 3.3 15.5c0 2.4.7 4.7 2 6.7L3 29l7-2.2c1.9 1 4 1.6 6.1 1.6h.01C23 28.4 28.7 22.8 28.7 16 28.7 8.6 23 3 16 3zm0 22.6h-.01c-1.9 0-3.7-.5-5.3-1.5l-.4-.2-4.2 1.3 1.3-4.1-.3-.4c-1.1-1.7-1.6-3.6-1.6-5.7 0-5.8 4.8-10.6 10.6-10.6 2.8 0 5.5 1.1 7.5 3.1s3.1 4.6 3.1 7.5c0 5.8-4.8 10.6-10.7 10.6zm5.8-7.9c-.3-.2-1.9-.9-2.2-1-.3-.1-.5-.2-.7.2s-.8 1-1 1.2c-.2.2-.4.2-.7.1-.3-.2-1.3-.5-2.5-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.4.4-.5.1-.2.2-.3.3-.5.1-.2.1-.4 0-.5 0-.2-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.2.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.9-.8 2.1-1.5.3-.7.3-1.4.2-1.5-.1-.2-.3-.2-.6-.4z"/>
        </svg>
      </Circle>
    </span>
  );
}

function readPreferredChannels() {
  try {
    const raw = localStorage.getItem(PREFERRED_STORAGE_KEY);
    const arr = JSON.parse(raw || '[]');
    return Array.isArray(arr) ? arr.filter(k => PREFERRED_META[k]) : [];
  } catch { return []; }
}

function writePreferredChannels(keys) {
  try { localStorage.setItem(PREFERRED_STORAGE_KEY, JSON.stringify(keys)); } catch { /* ignore */ }
}

function firstUrl(value) {
  const raw = String(value || '').trim();
  const match = raw.match(/https?:\/\/[^\s]+/i);
  return match ? match[0].replace(/[),.;]+$/, '') : '';
}

function normalizeChannelHref(key, value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const extracted = firstUrl(raw);
  if (extracted) return extracted;
  if (key === 'support_email') return raw.includes('@') ? `mailto:${raw.replace(/^mailto:/i, '')}` : '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (key === 'telegram') return `https://t.me/${raw.replace(/^@/, '').replace(/^t\.me\//i, '')}`;
  if (key === 'whatsapp') {
    if (/^(wa\.me|api\.whatsapp\.com|chat\.whatsapp\.com|whatsapp\.com)\//i.test(raw)) return `https://${raw}`;
    const digits = raw.replace(/[^\d+]/g, '').replace(/^\+/, '');
    return digits ? `https://wa.me/${digits}` : '';
  }
  return `https://${raw}`;
}

const agentUnreadKey = slotsAgentUnreadKey;

const normalizeSupportLabel = (value = '') => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');
const GENERIC_SUPPORT_LABELS = new Set(['support', 'support team', 'support agent', 'live support', 'live agent', 'live agents']);
const agentSubtitle = (agent = {}) => {
  const latest = String(agent.unread?.lastMessage || '').trim();
  if (latest) return latest;
  const name = normalizeSupportLabel(agent.name);
  const title = normalizeSupportLabel(agent.title || 'Support Agent');
  if (!title || title === name) return '';
  if (GENERIC_SUPPORT_LABELS.has(name) && GENERIC_SUPPORT_LABELS.has(title)) return '';
  return agent.title || 'Support Agent';
};

/* -------------------- PICKER MODAL -------------------- */
function SupportChannelPicker({ socials, icons = {}, initialSelected, onSave, onClose }) {
  const available = Object.keys(PREFERRED_META).filter(k => socials[k]);
  const [picked, setPicked] = useState(() => new Set(initialSelected));

  const toggle = (key) => {
    const next = new Set(picked);
    if (next.has(key)) next.delete(key); else next.add(key);
    setPicked(next);
  };
  const save = () => onSave(available.filter(k => picked.has(k)));

  return (
    <div
      className="fixed inset-0 z-[75] flex items-end sm:items-center justify-center px-4"
      style={{ background: 'rgba(3,5,12,0.6)', backdropFilter: 'blur(14px)', WebkitBackdropFilter: 'blur(14px)' }}
      onClick={onClose}
      data-testid="support-picker"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full sm:max-w-md max-h-[72vh] overflow-hidden rounded-t-3xl sm:rounded-3xl flex flex-col animate-fade-in-up"
        style={{
          background: 'var(--vl-bg, #000000)',
          border: `1px solid ${SUPPORT_ACCENT_BORDER}`,
          boxShadow: '0 -14px 40px rgba(0,0,0,0.55)'
        }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b" style={{ borderColor: SUPPORT_ACCENT_BORDER }}>
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#fff' }}>Preferred Social Supports</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.68)' }}>Select one or more channels - they'll appear below the agents</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', color: '#fff' }} data-testid="support-picker-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto p-4 space-y-2" data-testid="support-picker-list">
          {available.length === 0 && (
            <div className="py-10 text-center">
              <Megaphone className="w-10 h-10 mx-auto mb-3 opacity-70" style={{ color: SUPPORT_ACCENT_TEXT }} />
              <p className="display font-bold text-white/80">No social channels configured</p>
              <p className="text-sm text-white/50 mt-1">An admin can add them in Branding.</p>
            </div>
          )}
          {available.map(key => {
            const meta = PREFERRED_META[key];
            const url = socials[key];
            const customIcon = icons[key];
            const on = picked.has(key);
            return (
              <button
                key={key}
                onClick={() => toggle(key)}
                className="w-full flex items-center gap-3 rounded-2xl p-3 transition active:scale-[0.99] hover:brightness-110"
                style={{
                  background: on ? SUPPORT_ACCENT_DIM : 'rgba(255,255,255,0.03)',
                  border: on ? `1px solid ${SUPPORT_ACCENT_STRONG_BORDER}` : `1px solid ${SUPPORT_ACCENT_BORDER}`,
                }}
                data-testid={`support-picker-${key}`}
              >
                <span className="shrink-0 w-11 h-11 rounded-xl flex items-center justify-center overflow-hidden" style={{ background: customIcon ? 'rgba(0,0,0,0.55)' : (meta.Glyph ? meta.gradient : SUPPORT_ACCENT_GRADIENT), border: `1px solid ${SUPPORT_ACCENT_BORDER}`, boxShadow: '0 4px 14px rgba(0,0,0,0.35)' }}>
                  {customIcon ? (
                    <SafeImage src={customIcon} alt="" className="h-full w-full object-cover" loading="lazy" fallback={meta.Glyph ? <meta.Glyph /> : <MessagesSquare className="w-5 h-5 text-white" />} />
                  ) : meta.Glyph ? <meta.Glyph /> : <MessagesSquare className="w-5 h-5 text-white" />}
                </span>
                <div className="flex-1 min-w-0 text-left">
                  <p className="font-bold text-white">{meta.label}</p>
                  <p className="text-xs text-white/60 truncate">{String(url).replace(/^https?:\/\//, '').replace(/^mailto:/, '')}</p>
                </div>
                <span
                  className="shrink-0 w-6 h-6 rounded-md flex items-center justify-center"
                  style={{
                    background: on ? SUPPORT_ACCENT_GRADIENT : 'transparent',
                    border: on ? `1px solid ${SUPPORT_ACCENT_STRONG_BORDER}` : '1px solid rgba(255,255,255,0.25)',
                  }}
                >
                  {on && <Check className="w-4 h-4 text-white" />}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4 border-t flex gap-3" style={{ borderColor: SUPPORT_ACCENT_BORDER }}>
          <Button onClick={onClose} variant="outline" className="flex-1" style={{ borderColor: 'rgba(255,255,255,0.2)', color: '#fff', background: 'transparent' }} data-testid="support-picker-cancel">
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={available.length === 0}
            className="flex-1 font-bold"
            style={{ background: SUPPORT_ACCENT_GRADIENT, color: 'var(--vl-btn-primary-text, #031A2A)' }}
            data-testid="support-picker-save"
          >
            Save ({picked.size})
          </Button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- SUPPORT MENU (default export) -------------------- */
export default function SupportMenu({ branding, chatUnread = 0, agentUnread = [], attributedAgent = null, onLiveChat, onPickAgent, onOpenChannel, onManagePreferred, onClose }) {
  const rawSupport = branding?.support || {};
  const supportVisibility = branding?.support_visibility || {};
  const supportIcons = branding?.support_icons || {};
  const brandLogo = branding?.logo_url || branding?.intro_logo_url || '';
  const socials = {
    instagram: rawSupport.instagram || '',
    youtube: rawSupport.youtube || '',
    tiktok: rawSupport.tiktok || '',
    facebook: rawSupport.facebook || rawSupport.facebook_url || '',
    whatsapp: rawSupport.whatsapp || rawSupport.whatsapp_url || '',
    discord: rawSupport.discord || rawSupport.discord_url || '',
    telegram: rawSupport.telegram || rawSupport.telegram_url || '',
    support_email: rawSupport.support_email || rawSupport.email || '',
  };
  const [selected, setSelected] = useState(() => readPreferredChannels());
  const [pickerOpen, setPickerOpen] = useState(false);
  const chatAgents = useMemo(() => {
    const raw = Array.isArray(branding?.chat_agents) ? branding.chat_agents : [];
    return raw
      .filter(agent => agent && agent.enabled !== false && String(agent.name || '').trim())
      .map((agent, index) => ({
        id: agent.id || `agent_${index}`,
        name: String(agent.name || '').trim(),
        title: agent.title || 'Support Agent',
        avatar_url: agent.avatar_url || '',
        bio: agent.bio || '',
      }));
  }, [branding]);
  const attributedSupportAgent = useMemo(() => {
    if (!attributedAgent?.id && !attributedAgent?.name) return null;
    return {
      id: attributedAgent.id || 'assigned-agent',
      name: String(attributedAgent.name || 'Your Agent').trim(),
      title: attributedAgent.title || 'Your Agent',
      avatar_url: attributedAgent.avatar_url || '',
      bio: attributedAgent.bio || '',
      attributed: true,
    };
  }, [attributedAgent]);
  const otherSupportAgents = useMemo(() => {
    const rows = [];
    const seen = new Set();
    const seenNames = new Set();
    const addAgent = (agent) => {
      if (!agent?.id && !agent?.name) return;
      const key = agentUnreadKey(agent.id, agent.name);
      const nameKey = normalizeSupportLabel(agent.name);
      if (seen.has(key) || (nameKey && seenNames.has(nameKey))) return;
      seen.add(key);
      if (nameKey) seenNames.add(nameKey);
      rows.push(agent);
    };
    chatAgents
      .filter((agent) => (
        !attributedSupportAgent
        || (String(agent.id || '') !== String(attributedSupportAgent.id || '')
          && normalizeSupportLabel(agent.name) !== normalizeSupportLabel(attributedSupportAgent.name))
      ))
      .forEach(addAgent);
    return rows.length ? rows : [{ id: 'support-team', name: 'Support Team', title: 'Live Chat Agent', avatar_url: '' }];
  }, [attributedSupportAgent, chatAgents]);
  const unreadByAgent = useMemo(() => {
    const map = new Map();
    (Array.isArray(agentUnread) ? agentUnread : []).filter(Boolean).forEach(item => {
      const key = agentUnreadKey(item.agent_id, item.agent_name);
      map.set(key, {
        count: Number(item.unread_count || 0),
        lastMessage: item.last_message || '',
        lastMessageAt: item.last_message_at || '',
        avatar_url: item.avatar_url || '',
        bio: item.agent_bio || '',
      });
    });
    return map;
  }, [agentUnread]);
  const unreadOnlyAgents = useMemo(() => {
    const visibleKeys = new Set(
      [attributedSupportAgent, ...otherSupportAgents]
        .filter(Boolean)
        .map(agent => agentUnreadKey(agent.id, agent.name))
        .filter(Boolean)
    );
    return (Array.isArray(agentUnread) ? agentUnread : [])
      .filter(item => item && Number(item.unread_count || 0) > 0)
      .map((item, index) => ({
        id: String(item.agent_id || '').trim() || `unread-agent-${index}`,
        name: String(item.agent_name || 'Support Team').trim(),
        title: item.agent_title || 'Support Agent',
        avatar_url: item.avatar_url || '',
        bio: item.agent_bio || '',
      }))
      .filter(agent => !visibleKeys.has(agentUnreadKey(agent.id, agent.name)));
  }, [agentUnread, attributedSupportAgent, otherSupportAgents]);
  const referredAgentRow = useMemo(() => {
    if (!attributedSupportAgent) return null;
    const unread = unreadByAgent.get(agentUnreadKey(attributedSupportAgent.id, attributedSupportAgent.name))
      || unreadByAgent.get(agentUnreadKey(null, attributedSupportAgent.name))
      || { count: 0, lastMessage: '', lastMessageAt: '' };
    return { ...attributedSupportAgent, avatar_url: unread.avatar_url || attributedSupportAgent.avatar_url, bio: unread.bio || attributedSupportAgent.bio, unread };
  }, [attributedSupportAgent, unreadByAgent]);
  const supportAgentRows = useMemo(() => {
    return [...otherSupportAgents, ...unreadOnlyAgents]
      .map(agent => {
        const unread = unreadByAgent.get(agentUnreadKey(agent.id, agent.name)) || unreadByAgent.get(agentUnreadKey(null, agent.name)) || { count: 0, lastMessage: '', lastMessageAt: '' };
        return {
          ...agent,
          avatar_url: unread.avatar_url || agent.avatar_url,
          bio: unread.bio || agent.bio,
          unread,
        };
      })
      .sort((a, b) => {
        const unreadDiff = (b.unread?.count || 0) - (a.unread?.count || 0);
        if (unreadDiff) return unreadDiff;
        const aTime = Date.parse(a.unread?.lastMessageAt || '') || 0;
        const bTime = Date.parse(b.unread?.lastMessageAt || '') || 0;
        return bTime - aTime;
      });
  }, [otherSupportAgents, unreadOnlyAgents, unreadByAgent]);

  const activeSelected = useMemo(
    () => selected.filter(k => socials[k] && PREFERRED_META[k] && supportVisibility[k] !== false),
    [selected, socials, supportVisibility]
  );

  // Sync back to storage if admin removed a link
  useEffect(() => {
    if (activeSelected.length !== selected.length) {
      setSelected(activeSelected);
      writePreferredChannels(activeSelected);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSelected.length]);

  const savePicker = (keys) => {
    setSelected(keys);
    writePreferredChannels(keys);
    setPickerOpen(false);
  };

  const openChannel = (key) => {
    if (onOpenChannel) {
      onOpenChannel(key);
      return;
    }
    const url = socials[key];
    if (!url) return;
    const href = normalizeChannelHref(key, url);
    if (!href) return;
    onClose?.();
    setTimeout(() => window.open(href, '_blank', 'noopener,noreferrer'), 120);
  };

  const pickAgent = (agent) => {
    if (onPickAgent) onPickAgent(agent);
    else onLiveChat?.();
  };

  return (
    <div className="flex flex-col h-full">
      <SheetHeader className="px-5 pr-20 pt-5 pb-3 border-b" style={{ borderColor: SUPPORT_ACCENT_BORDER }}>
        <div className="flex items-start gap-3">
          <div
            className="relative h-[52px] w-[52px] shrink-0 overflow-visible rounded-2xl"
            style={{ filter: 'none' }}
            aria-hidden
          >
            <div className="h-[52px] w-[52px] overflow-hidden rounded-2xl border border-white/10 bg-black/45">
              {brandLogo ? (
                <SafeImage
                  src={brandLogo}
                  alt=""
                  className="h-full w-full object-cover"
                  data-testid="support-brand-logo"
                  loading="lazy"
                  fallback={<MessagesSquare className="m-3 h-7 w-7 text-white/75" />}
                />
              ) : (
                <MessagesSquare className="m-3 h-7 w-7 text-white/75" />
              )}
            </div>
            <span
              className="absolute -bottom-1 -right-1 h-4 w-4 rounded-full border-2 border-[#0B3D2A] bg-[#22C55E]"
              style={{ boxShadow: 'none' }}
              data-testid="support-online-dot"
            />
          </div>
          <div className="min-w-0 pt-0.5 text-left">
            <SheetTitle className="display text-2xl font-extrabold leading-tight" style={{ color: '#fff' }}>TALK WITH AGENTS</SheetTitle>
            <p className="text-sm" style={{ color: 'rgba(255,255,255,0.68)' }}>Pick a channel to reach our team</p>
          </div>
        </div>
      </SheetHeader>

      <div className="p-4 space-y-3 overflow-y-auto" data-testid="support-menu">
        {referredAgentRow && (
          <section className="space-y-2" data-testid="support-referred-agent">
            <div className="flex items-center gap-3 px-1 pt-1">
              <span className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl border bg-black/45" style={{ borderColor: SUPPORT_ACCENT_BORDER }}>
                <SafeImage src={MAIN_AGENT_MASCOT_AVATAR} alt="" className="h-full w-full object-cover" loading="lazy" fallback={<UserRound className="h-6 w-6 text-white/75" />} />
              </span>
              <div className="min-w-0 text-left"><p className="display text-base font-black tracking-wide text-white">YOUR MAIN AGENT</p><p className="text-xs" style={{ color: 'rgba(255,255,255,0.68)' }}>Your account is connected only to this agent</p></div>
            </div>
            <button type="button" onClick={() => pickAgent(referredAgentRow)} className="w-full flex items-center gap-3 rounded-2xl p-3 text-left transition active:scale-[0.99] hover:bg-white/5" style={{ border: `1px solid ${SUPPORT_ACCENT_STRONG_BORDER}`, background: SUPPORT_ACCENT_DIM }} data-testid={`support-agent-${referredAgentRow.id}`}>
              <span className="relative h-14 w-14 shrink-0 overflow-visible"><span className="grid h-14 w-14 place-items-center overflow-hidden rounded-full" style={{ background: SUPPORT_ACCENT_DIM, border: `1px solid ${SUPPORT_ACCENT_STRONG_BORDER}` }}><SafeImage src={referredAgentRow.avatar_url || MAIN_AGENT_MASCOT_AVATAR} alt="" className="h-full w-full object-cover" loading="lazy" fallback={<UserRound className="h-7 w-7 text-white" />} /></span><span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2" style={{ background: SUPPORT_ONLINE_GREEN, borderColor: '#0B3D2A' }} /></span>
              <span className="min-w-0 flex-1"><span className="block truncate text-base font-extrabold text-white">{referredAgentRow.name}</span>{agentSubtitle(referredAgentRow) && <span className="block truncate text-xs text-white/65">{agentSubtitle(referredAgentRow)}</span>}{referredAgentRow.bio && <span className="mt-0.5 block max-h-10 overflow-hidden text-xs leading-4 text-white/60">{referredAgentRow.bio}</span>}<span className="mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em]" style={{ background: SUPPORT_ACCENT_DIM, color: SUPPORT_ACCENT_TEXT, border: `1px solid ${SUPPORT_ACCENT_BORDER}` }}>Your main agent</span></span>
              {(referredAgentRow.unread?.count || 0) > 0 && <span className="min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-black text-white flex items-center justify-center" style={{ background: '#FF3D71', border: '2px solid #fff' }}>{referredAgentRow.unread.count > 9 ? '9+' : referredAgentRow.unread.count}</span>}<ChevronDown className="-rotate-90 h-5 w-5 text-white/55" />
            </button>
          </section>
        )}

        <div className="flex items-center gap-3 px-1 pt-1" data-testid="support-agents-heading">
          <span
            className="grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-2xl border bg-black/45"
            style={{ borderColor: SUPPORT_ACCENT_BORDER, boxShadow: 'none' }}
            aria-hidden
          >
            {brandLogo ? (
              <SafeImage
                src={brandLogo}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                fallback={<UserRound className="h-6 w-6 text-white/75" />}
              />
            ) : (
              <UserRound className="h-6 w-6 text-white/75" />
            )}
          </span>
          <div className="min-w-0 text-left">
            <p className="display text-base font-black tracking-wide text-white">{referredAgentRow ? 'SUPPORT TEAM' : 'OUR AGENTS'}</p>
            <p className="text-xs" style={{ color: 'rgba(255,255,255,0.68)' }}>
              {chatUnread > 0 ? `${chatUnread} unread live chat message${chatUnread === 1 ? '' : 's'}` : referredAgentRow ? 'These contacts do not change your main-agent link' : 'Choose who you want to text'}
            </p>
          </div>
        </div>
        <div className="space-y-2 rounded-3xl border bg-black/20 p-2 animate-fade-in-up" style={{ borderColor: SUPPORT_ONLINE_BORDER, boxShadow: 'none' }} data-testid="support-agent-list">
          {supportAgentRows.map(agent => (
            <button
              key={agent.id}
              type="button"
              onClick={() => pickAgent(agent)}
              className="w-full flex items-center gap-3 rounded-2xl p-3 text-left transition active:scale-[0.99] hover:bg-white/5"
              style={{ border: `1px solid ${SUPPORT_ONLINE_BORDER}`, background: 'rgba(255,255,255,0.035)', boxShadow: 'none' }}
              data-testid={`support-agent-${agent.id}`}
            >
              <span className="relative h-12 w-12 shrink-0 overflow-visible">
                <span className="grid h-12 w-12 place-items-center overflow-hidden rounded-full" style={{ background: SUPPORT_ACCENT_DIM, border: `1px solid ${SUPPORT_ONLINE_BORDER}`, boxShadow: 'none' }}>
                  {agent.avatar_url ? (
                    <SafeImage src={agent.avatar_url} alt="" className="h-full w-full object-cover" loading="lazy" fallback={<UserRound className="h-6 w-6 text-white" />} />
                  ) : (
                    <UserRound className="h-6 w-6 text-white" />
                  )}
                </span>
                <span
                  className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full border-2"
                  style={{ background: SUPPORT_ONLINE_GREEN, borderColor: '#0B3D2A', boxShadow: 'none' }}
                  aria-label={`${agent.name} online`}
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-base font-extrabold text-white">{agent.name}</span>
                {agentSubtitle(agent) && (
                  <span className="block truncate text-xs text-white/65">{agentSubtitle(agent)}</span>
                )}
                {agent.bio && (
                  <span className="mt-0.5 block max-h-8 overflow-hidden text-xs leading-4 text-white/50">{agent.bio}</span>
                )}
                {agent.attributed && (
                  <span
                    className="mt-1 inline-flex rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em]"
                    style={{ background: SUPPORT_ACCENT_DIM, color: SUPPORT_ACCENT_TEXT, border: `1px solid ${SUPPORT_ACCENT_BORDER}` }}
                  >
                    Your main agent
                  </span>
                )}
              </span>
              {(agent.unread?.count || 0) > 0 && (
                <span
                  className="min-w-[24px] h-6 px-1.5 rounded-full text-[11px] font-black text-white flex items-center justify-center"
                  style={{ background: '#FF3D71', border: '2px solid #fff', boxShadow: '0 0 12px rgba(255,61,113,0.72)' }}
                  data-testid={`support-agent-${agent.id}-badge`}
                >
                  {agent.unread.count > 9 ? '9+' : agent.unread.count}
                </span>
              )}
              <ChevronDown className="-rotate-90 h-5 w-5 text-white/55" />
            </button>
          ))}
        </div>

        {activeSelected.map(key => {
          const meta = PREFERRED_META[key];
          const url = socials[key];
          const customIcon = supportIcons[key];
          return (
            <button
              key={key}
              onClick={() => openChannel(key)}
              className="w-full flex items-center gap-4 p-4 rounded-2xl transition active:scale-[0.99] hover:brightness-110 animate-fade-in-up"
              style={{
                background: `linear-gradient(135deg, ${SUPPORT_ACCENT_DIM}, color-mix(in oklab, var(--vl-bg, #000000) 86%, ${SUPPORT_ACCENT}))`,
                border: `1px solid ${SUPPORT_ACCENT_BORDER}`,
                boxShadow: 'none',
              }}
              data-testid={`support-pref-${key}`}
            >
              <div
                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 overflow-hidden"
                style={{ background: customIcon ? 'rgba(0,0,0,0.55)' : (meta.Glyph ? meta.gradient : SUPPORT_ACCENT_GRADIENT), border: `1px solid ${SUPPORT_ACCENT_BORDER}`, boxShadow: '0 4px 14px rgba(0,0,0,0.35)' }}
              >
                {customIcon ? (
                  <SafeImage src={customIcon} alt="" className="h-full w-full object-cover" data-testid={`support-pref-${key}-icon`} loading="lazy" fallback={meta.Glyph ? <meta.Glyph /> : <MessagesSquare className="w-6 h-6 text-white" />} />
                ) : meta.Glyph ? <meta.Glyph /> : <MessagesSquare className="w-6 h-6 text-white" />}
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="display text-base font-bold" style={{ color: '#fff' }}>{meta.label}</p>
                <p className="text-xs mt-0.5 truncate" style={{ color: 'rgba(255,255,255,0.68)' }}>
                  {String(url).replace(/^https?:\/\//, '').replace(/^mailto:/, '')}
                </p>
              </div>
              <span className="shrink-0 display text-xs font-bold px-3 py-1.5 rounded-full" style={{ background: SUPPORT_ACCENT_DIM, color: SUPPORT_ACCENT_TEXT, border: `1px solid ${SUPPORT_ACCENT_BORDER}` }}>{meta.cta}</span>
            </button>
          );
        })}

      </div>

      <div className="mt-auto p-4">
        <Button onClick={onClose} variant="outline" className="w-full" style={{ borderColor: SUPPORT_ACCENT_BORDER, color: '#fff', background: 'transparent' }}>Close</Button>
      </div>

      {pickerOpen && (
        <SupportChannelPicker
          socials={Object.fromEntries(Object.entries(socials).filter(([key, url]) => url && supportVisibility[key] !== false))}
          icons={supportIcons}
          initialSelected={selected}
          onSave={savePicker}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
